import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, Fragment } from 'react';
import { Alert, Collapse, type AlertProps } from '@mui/material';
import { composeSx } from '../../utils/composeSx';
import { HFBox } from '../HFBox';

export type HFAlertSeverity = 'success' | 'info' | 'warning' | 'error';

export const HF_ALERT_AUTO_HIDE_MS = 5000;

export type HFAlertProps = AlertProps & {
  /** Render in place instead of the bottom toast stack. */
  inline?: boolean;
  /** Auto-dismiss delay in ms. `null` keeps the toast until closed. Default 5000. */
  autoHideMs?: number | null;
};

export type HFAlertPushInput = {
  severity?: HFAlertSeverity;
  message: ReactNode;
  /** Auto-dismiss delay in ms. `null` keeps the toast until closed. Default 5000. */
  autoHideMs?: number | null;
};

type StackItem = {
  id: number;
  severity: HFAlertSeverity;
  message: ReactNode;
  open: boolean;
  autoHideMs: number | null;
};

type AlertStackValue = {
  push: (input: HFAlertPushInput) => number;
  dismiss: (id: number) => void;
};

const AlertStackContext = createContext<AlertStackValue | undefined>(undefined);

let nextAlertId = 1;

const ALERT_SURFACE_SX = {
  backgroundImage: 'none',
  '& .MuiAlert-message': { whiteSpace: 'pre-wrap' },
};

function AlertMessage({ message }: { message: ReactNode }) {
  if (typeof message !== 'string') return message;
  const lines = message.replace(/\\n/g, '\n').split('\n');
  return (
    <>
      {lines.map((line, index) => (
        <Fragment key={index}>
          {index > 0 ? <br /> : null}
          {line}
        </Fragment>
      ))}
    </>
  );
}

export function HFAlertProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<StackItem[]>([]);
  const hideTimers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = hideTimers.current.get(id);
    if (timer != null) {
      window.clearTimeout(timer);
      hideTimers.current.delete(id);
    }
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, open: false } : item)));
  }, []);

  const remove = useCallback((id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const push = useCallback(
    ({ severity = 'info', message, autoHideMs = HF_ALERT_AUTO_HIDE_MS }: HFAlertPushInput) => {
      const id = nextAlertId++;
      setItems((prev) => [...prev, { id, severity, message, open: true, autoHideMs }]);
      if (autoHideMs != null && autoHideMs > 0) {
        hideTimers.current.set(
          id,
          window.setTimeout(() => {
            hideTimers.current.delete(id);
            dismiss(id);
          }, autoHideMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <AlertStackContext.Provider value={value}>
      {children}
      <HFBox
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.snackbar,
          display: 'flex',
          flexDirection: 'column-reverse',
          alignItems: 'center',
          gap: 1,
          p: 2,
          pointerEvents: 'none',
        }}
      >
        {items.map((item) => (
          <Collapse
            key={item.id}
            in={item.open}
            timeout={220}
            unmountOnExit
            onExited={() => remove(item.id)}
            sx={{
              width: 'max-content',
              maxWidth: 'min(560px, calc(100vw - 32px))',
              pointerEvents: 'auto',
            }}
          >
            <Alert
              severity={item.severity}
              variant="filled"
              onClose={() => dismiss(item.id)}
              sx={{
                ...ALERT_SURFACE_SX,
                position: 'relative',
                overflow: 'hidden',
                width: 'max-content',
                maxWidth: '100%',
              }}
            >
              <AlertMessage message={item.message} />
              {item.autoHideMs != null && item.autoHideMs > 0 ? (
                <HFBox
                  role="progressbar"
                  aria-label="Time until dismiss"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: 4,
                    bgcolor: 'currentColor',
                    opacity: 0.75,
                    transformOrigin: 'left center',
                    animation: `hfAlertShrink ${item.autoHideMs}ms linear forwards`,
                    '@keyframes hfAlertShrink': {
                      from: { transform: 'scaleX(1)' },
                      to: { transform: 'scaleX(0)' },
                    },
                  }}
                />
              ) : null}
            </Alert>
          </Collapse>
        ))}
      </HFBox>
    </AlertStackContext.Provider>
  );
}

export function useHFAlert(): AlertStackValue {
  const ctx = useContext(AlertStackContext);
  if (!ctx) throw new Error('useHFAlert must be used within an HFThemeProvider');
  return ctx;
}

export function HFAlert({
  severity = 'info',
  variant = 'filled',
  inline = false,
  autoHideMs = HF_ALERT_AUTO_HIDE_MS,
  children,
  sx,
  ...props
}: HFAlertProps) {
  const stack = useContext(AlertStackContext);

  useEffect(() => {
    if (!stack || inline) return;
    const id = stack.push({ severity, message: children, autoHideMs });
    return () => stack.dismiss(id);
    // Toast once for this mount; updates should use useHFAlert().push.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only enqueue
  }, [stack, inline]);

  if (stack && !inline) return null;

  return (
    <Alert severity={severity} variant={variant} sx={composeSx(ALERT_SURFACE_SX, sx)} {...props}>
      <AlertMessage message={children} />
    </Alert>
  );
}
